import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiMail, FiLock, FiAlertCircle, FiCheckCircle, FiArrowRight } from "react-icons/fi";

import { setCredentials } from "../features/auth/authSlice";
import { useLoginMutation } from "../services/api";
import logo from "../assets/melona logo.png";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [login, { isLoading, error }] = useLoginMutation();

  // Set page title
  React.useEffect(() => {
    document.title = "Admin Login - Melona Printing";
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const result = await login(form).unwrap();
      dispatch(setCredentials(result));
      navigate("/dashboard");
    } catch (_error) {
      // The mutation error state already powers the inline message.
    }
  };

  return (
    <section className="premium-login-page">
      <div className="premium-login-container">
        {/* Left Side - Branding */}
        <div className="premium-login-branding">
          <div className="premium-login-branding__overlay" />
          <div className="premium-login-branding__content">
            <div className="premium-login-logo">
              <img src={logo} alt="Melona" style={{ height: "50px", width: "auto" }} />
            </div>
            <h1 className="premium-login-branding__title">
              Manage Your Printing Business with Excellence
            </h1>
            <p className="premium-login-branding__subtitle">
              Streamline orders, track inventory, manage customers, and grow your printing business efficiently with our powerful admin dashboard.
            </p>
            <div className="premium-login-features">
              <div className="premium-login-feature">
                <div className="premium-login-feature__icon">
                  <FiCheckCircle />
                </div>
                <span>Order Management</span>
              </div>
              <div className="premium-login-feature">
                <div className="premium-login-feature__icon">
                  <FiCheckCircle />
                </div>
                <span>Customer Tracking</span>
              </div>
              <div className="premium-login-feature">
                <div className="premium-login-feature__icon">
                  <FiCheckCircle />
                </div>
                <span>Inventory Control</span>
              </div>
              <div className="premium-login-feature">
                <div className="premium-login-feature__icon">
                  <FiCheckCircle />
                </div>
                <span>Financial Reports</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="premium-login-form-side">
          <form className="premium-login-form" onSubmit={handleSubmit}>
            <div className="premium-login-form__header">
              <span className="premium-login-eyebrow">Admin Access</span>
              <h2 className="premium-login-title">Welcome Back</h2>
              <p className="premium-login-subtitle">
                Sign in to access your dashboard and manage operations
              </p>
            </div>

            {error && (
              <div className="premium-login-alert premium-login-alert--error">
                <FiAlertCircle />
                <span>{error?.data?.message || "Unable to login. Please check your credentials."}</span>
              </div>
            )}

            <div className="premium-login-form__fields">
              <div className="premium-login-field">
                <label className="premium-login-label" htmlFor="email">
                  Email Address <span className="premium-login-required">*</span>
                </label>
                <div className="premium-login-input-wrapper">
                  <div className="premium-login-input-icon">
                    <FiMail />
                  </div>
                  <input
                    id="email"
                    className="premium-login-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    type="email"
                    placeholder="Enter your email"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="premium-login-field">
                <label className="premium-login-label" htmlFor="password">
                  Password <span className="premium-login-required">*</span>
                </label>
                <div className="premium-login-input-wrapper">
                  <div className="premium-login-input-icon">
                    <FiLock />
                  </div>
                  <input
                    id="password"
                    className="premium-login-input"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="premium-login-input-action"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button 
                className="premium-login-submit"
                type="submit" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="premium-login-spinner"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In <FiArrowRight />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="premium-login-footer">
            <p>© {new Date().getFullYear()} Melona Printing. All rights reserved.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
